import { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";
import { X } from "lucide-react";

export function useCustomerFetch(path, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!!path);

  const reload = useCallback(() => {
    if (!path) { setData(null); setLoading(false); return; }
    setLoading(true);
    api.get(path).then((r) => setData(r.data)).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  useEffect(() => { reload(); }, [reload, ...deps]);

  return { data, loading, reload };
}

export function SectionLabel({ children, className = "" }) {
  return (
    <div className={"text-[11px] uppercase font-bold tracking-wider text-slate-500 " + className}
      style={{ fontFamily: "'JetBrains Mono', monospace" }}>
      {children}
    </div>
  );
}

export function Card({ children, className = "", ...rest }) {
  return (
    <div
      className={"bg-white rounded-2xl border border-slate-200/80 shadow-[0_4px_20px_-2px_rgba(13,71,33,0.06)] " + className}
      {...rest}
    >
      {children}
    </div>
  );
}

const STATUS_MAP = {
  live_healthy: { bg: "bg-[#DCFCE7]", text: "text-[#15803D]", border: "border-[#86EFAC]", label: "Live" },
  in_progress: { bg: "bg-[#FEF3C7]", text: "text-[#B45309]", border: "border-[#FCD34D]", label: "In Progress" },
  open_complaint: { bg: "bg-[#E0F2FE]", text: "text-[#0369A1]", border: "border-[#7DD3FC]", label: "Open" },
  resolved: { bg: "bg-[#F0FDF4]", text: "text-[#166534]", border: "border-[#BBF7D0]", label: "Resolved" },
  action_required: { bg: "bg-[#FEE2E2]", text: "text-[#B91C1C]", border: "border-[#FCA5A5]", label: "Action Needed" },
  on_hold: { bg: "bg-[#F1F5F9]", text: "text-[#475569]", border: "border-[#CBD5E1]", label: "On Hold" },
  quote_ready: { bg: "bg-amber-100", text: "text-amber-700", border: "border-amber-300", label: "Review Quote" },
};

export const TICKET_STATUS_TONE = {
  open: "open_complaint",
  in_progress: "in_progress",
  on_hold: "on_hold",
  resolution_support_required: "action_required",
  completed: "resolved",
  closed: "resolved",
};

export const TICKET_STATUS_LABEL = {
  open: "Open",
  in_progress: "In Progress",
  on_hold: "On Hold",
  resolution_support_required: "Action Needed",
  completed: "Resolved",
  closed: "Closed",
};

export function StatusBadge({ tone, children, className = "" }) {
  const t = STATUS_MAP[tone] || STATUS_MAP.on_hold;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${t.bg} ${t.text} ${t.border} ${className}`}
    >
      {children}
    </span>
  );
}

const SMILEYS = [
  { rating: 1, emoji: "😡", label: "Very Poor", color: "text-red-500 hover:bg-red-50" },
  { rating: 2, emoji: "🙁", label: "Poor", color: "text-orange-500 hover:bg-orange-50" },
  { rating: 3, emoji: "😐", label: "Average", color: "text-amber-500 hover:bg-amber-50" },
  { rating: 4, emoji: "🙂", label: "Good", color: "text-emerald-500 hover:bg-emerald-50" },
  { rating: 5, emoji: "🤩", label: "Delighted", color: "text-emerald-700 hover:bg-emerald-100" },
];

export function SmileyRating({ value, onChange, disabled }) {
  return (
    <div className="flex items-center justify-between gap-1">
      {SMILEYS.map((s) => (
        <button
          key={s.rating}
          type="button"
          disabled={disabled}
          onClick={() => onChange?.(s.rating)}
          data-testid={`smiley-rating-${s.rating}`}
          className={
            "flex-1 flex flex-col items-center gap-1 py-2 rounded-xl text-2xl transition-all duration-150 " +
            s.color +
            (value === s.rating ? " ring-2 ring-emerald-600 bg-emerald-50 scale-110 shadow-sm" : " opacity-70") +
            (disabled ? " cursor-default" : " cursor-pointer active:scale-95")
          }
        >
          <span>{s.emoji}</span>
          <span className="text-[9px] font-semibold uppercase tracking-wide text-slate-500">{s.label}</span>
        </button>
      ))}
    </div>
  );
}

export function Sheet({ open, onClose, title, children, testid }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center" data-testid={testid}>
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-md max-h-[88vh] overflow-y-auto bg-white rounded-t-3xl sm:rounded-3xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] p-5 animate-in slide-in-from-bottom duration-300">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold tracking-tight text-slate-900">{title}</h3>
          <button onClick={onClose} data-testid="sheet-close" className="p-1.5 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <X className="w-4 h-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function EmptyState({ icon: Icon, title, subtitle, action }) {
  return (
    <div className="text-center py-12 px-4">
      {Icon && <Icon className="w-10 h-10 mx-auto text-emerald-600/40 mb-3" strokeWidth={1.5} />}
      <div className="text-base font-semibold text-slate-700">{title}</div>
      {subtitle && <div className="text-sm text-slate-500 mt-1">{subtitle}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function daysBetween(fromIso, toIso) {
  try {
    const a = new Date(fromIso);
    const b = toIso ? new Date(toIso) : new Date();
    return Math.floor((b - a) / (1000 * 60 * 60 * 24));
  } catch {
    return 0;
  }
}

/** Shared status label/tone for an item from GET /customer-app/requests
 * (unified Lead + Opportunity feed) — used by both the Home banner and the
 * Past Orders history tab, so the two stay visually consistent. */
export function requestStatusMeta(r) {
  if (r.quotation_pending) return { label: "Review Quotation", tone: "quote_ready" };
  if (r.installation_done) return { label: "Installed", tone: "live_healthy" };

  if (r.kind === "opportunity") {
    // NOTE: "won" is an internal sales-pipeline signal only — never treat it as
    // "done" to the customer. Only 100% activity completion means Completed.
    if (r.activities_total > 0 && r.activities_done === r.activities_total) return { label: "Completed", tone: "resolved" };
    if (r.status === "lost") return { label: "Lost", tone: "action_required" };
    if (r.status === "dropped") return { label: "Dropped", tone: "on_hold" };
    if (r.status === "on_hold") return { label: "On Hold", tone: "on_hold" };
    return { label: "Installation In Progress", tone: "in_progress" };
  }

  // kind === "lead"
  if (r.quotation_status === "accepted") return { label: "Quotation Accepted", tone: "resolved" };
  if (r.quotation_status === "rejected") return { label: "Quotation Declined", tone: "action_required" };
  if (r.status === "dropped") return { label: "Dropped", tone: "on_hold" };
  if (r.status === "on_hold") return { label: "On Hold", tone: "on_hold" };
  if (r.status === "open") return r.assigned ? { label: "Assigned — In Review", tone: "in_progress" } : { label: "Awaiting Consultant", tone: "open_complaint" };
  if (r.status === "accepted") return { label: "In Progress", tone: "in_progress" };
  if (r.status === "converted") return { label: "Processing", tone: "in_progress" };
  return { label: r.status || "Open", tone: "open_complaint" };
}
